import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Login from './components/Auth/Login';
import AdminDashboard from './components/Admin/AdminDashboard';
import OfficerDashboard from './components/Officer/OfficerDashboard';
import CollectorDashboard from './components/Collector/CollectorDashboard';
import PrivateRoute from './components/Auth/PrivateRoute';
import InstallPrompt from './components/Common/InstallPrompt';
import ErrorBoundary from './components/Common/ErrorBoundary';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';

// Admin theme with dark blue and white color scheme
const adminTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0D47A1', // Dark blue
      light: '#1565C0',
      dark: '#0A3D7A',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#1976D2', // Medium blue
      light: '#42A5F5',
      dark: '#1565C0',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0D47A1',
      secondary: '#424242',
    },
    success: {
      main: '#2E7D32',
      light: '#4CAF50',
      dark: '#1B5E20',
    },
    error: {
      main: '#C62828',
      light: '#E53935',
      dark: '#B71C1C',
    },
    warning: {
      main: '#F57C00',
      light: '#FF9800',
      dark: '#E65100',
    },
    info: {
      main: '#0277BD',
      light: '#0288D1',
      dark: '#01579B',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      color: '#0D47A1',
    },
    h5: {
      fontWeight: 600,
      color: '#0D47A1',
    },
    h6: {
      fontWeight: 600,
      color: '#0D47A1',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.3)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(13, 71, 161, 0.1)',
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
        },
      },
    },
  },
});

// Default theme for other roles
const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const theme = defaultTheme;

function App() {
  // Add global error handler
  React.useEffect(() => {
    const handleError = (event) => {
      console.error('Global error:', event.error);
    };

    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <ToastProvider>
            <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route
                  path="/admin/*"
                  element={
                    <PrivateRoute allowedRoles={['ADMIN']}>
                      <AdminDashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/officer/*"
                  element={
                    <PrivateRoute allowedRoles={['LOAN_OFFICER']}>
                      <OfficerDashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/collector/*"
                  element={
                    <PrivateRoute allowedRoles={['FIELD_OFFICER']}>
                      <CollectorDashboard />
                    </PrivateRoute>
                  }
                />
                <Route path="/" element={<Navigate to="/login" replace />} />
              </Routes>
            </Router>
            <InstallPrompt />
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;

